/* eslint-disable complexity */
import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import isUndefined from 'lodash/isUndefined';
import Textbox, { TextboxProps } from 'carbon-react/lib/components/textbox';

import * as styles from './ui-carbon-number-field.scss'

const MAX_LENGTH = 10;
const MAX_DECIMALS = 2;
const MIN_MAX_DEFAULT = 9999999999;
const NON_NUMERIC_SYMBOL_REGEX = /(?!^)-|[^0-9.-]/g;

export type UICarbonNumberFieldProps = Partial<{
  /**
   * @default 'decimal'
   */
  type: 'decimal' | 'number' | 'currency' | 'percentage';
  /**
   * @default MIN_MAX_DEFAULT
   */
  clampMax: number;
  /**
   * @default -MIN_MAX_DEFAULT
   */
  clampMin: number;
  /**
   * @default MAX_DECIMALS
   */
  maxDecimals: number;
  /**
   * @default MAX_LENGTH
   */
  maxLength: number;
  /**
   * @default '.'
   */
  decimalDelimiter: string;
  /**
   * @default '£'
   */
  currencySymbol: string;
  /**
   * @default '0'
   */
  value: string;
  /**
   * @default '0'
   */
  placeholder: string;
  /**
   *
   */
  onChange: (value: string) => void;
  /**
   *
   */
  onBlur: (value: string) => void;
}>;

type UINumberFieldProps = UICarbonNumberFieldProps & Omit<TextboxProps, keyof UICarbonNumberFieldProps>;

export const UICarbonNumberField = (props: UINumberFieldProps) => {
  const {
    value = '0',
    placeholder = '0',
    maxDecimals = MAX_DECIMALS,
    maxLength = MAX_LENGTH,
    decimalDelimiter = '.',
    currencySymbol = '£',
    type = 'decimal',
    clampMin = -MIN_MAX_DEFAULT,
    clampMax = MIN_MAX_DEFAULT,
    onChange,
    onBlur,
    className,
    style,
    ...rest
  } = props;

  /**
   * Sanitazes the input to a valid decimals, to a maximum stated amount.
   * @param {string|undefined} value
   */
  const sanitizeDecimals = useCallback(
    (value?: string): string => {
      if (value === undefined || type === 'number' || maxDecimals <= 0) {
        return '';
      }

      return value.slice(0, maxDecimals).replace(/\D/g, '');
    },
    [maxDecimals, type]
  );

  /**
   * Sanitazes the input to a valid integers, and clamps if needed.
   * @param {string} value
   */
  const sanitizeIntegers = useCallback(
    (value: string) => {
      // '-' shouldnt count as a digit
      const maxLengthNegativeAware = value.startsWith('-') ? maxLength + 1 : maxLength;
      const integers = value
        .replace(NON_NUMERIC_SYMBOL_REGEX, '') // removes any character that aren't a number or minus
        .slice(0, maxLengthNegativeAware);

      return integers;
    },
    [maxLength]
  );

  /**
   * Formats a certain number of integers and decimals.
   * @param {string} value
   */
  const formatNumber = useCallback(
    (value: string) => {
      const split = value.split(decimalDelimiter);
      const showDecimalDelimiter =
        type !== 'number' && maxDecimals! > 0 && decimalDelimiter && value.includes(decimalDelimiter);
      const displayDecimalDelimiter = showDecimalDelimiter ? decimalDelimiter : '';
      const integers = sanitizeIntegers(split[0]) as string;
      const decimals = split[1] && type !== 'number' ? sanitizeDecimals(split[1]) : '';

      return integers + displayDecimalDelimiter + decimals;
    },
    [decimalDelimiter, type, maxDecimals, sanitizeIntegers, sanitizeDecimals]
  );

  /**
   * Ensure integer value is within min and max clamp values.
   * @param {string} rawValue
   */
  const clampNumber = useCallback(
    (rawValue: string): string => {
      const value = formatNumber(rawValue);

      if (value == '' || value === '-0' || value === '-' || value === decimalDelimiter) {
        return value;
      }

      const parsedValue = parseFloat(value);

      if (!isUndefined(clampMin) && parsedValue < clampMin) {
        return clampMin.toString();
      } else if (!isUndefined(clampMax) && parsedValue > clampMax) {
        return clampMax.toString();
      }

      return value;
    },
    [clampMax, clampMin, decimalDelimiter, formatNumber]
  );

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;

    const formattedNumber = formatNumber(value);

    setInternalValue(formattedNumber);
    onChange?.(formattedNumber);
  };

  const onBlurInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;

    const clampedValue = clampNumber(value);
    setInternalValue(clampedValue);

    onBlur?.(clampedValue) ?? onChange?.(clampedValue);
  };

  const [internalValue, setInternalValue] = useState(clampNumber(value));

  useEffect(() => {
    setInternalValue(formatNumber(value));
  }, [formatNumber, value]);

  return (
    <div
      className={clsx(className, {
        [styles.isPercentage]: type === 'percentage'
      })}
      style={style}
    >
      <Textbox
        {...rest}
        onChange={onChangeInput}
        onBlur={onBlurInput}
        value={internalValue}
        placeholder={placeholder}
        prefix={type === 'currency' ? currencySymbol : type === 'percentage' ? '%' : undefined}
      />
    </div>
  );
};
